// Normalizes orientation so storage ALWAYS holds the exact pixels the editor
// displays — upright, EXIF-free.
//
// Why "the exact pixels the editor displays": the editor renders a plain <img>,
// and the browser auto-applies EXIF orientation when decoding <img> — INCLUDING
// partial/garbled tags that a metadata reader (exifr) misses, and the no-tag
// case (WhatsApp strips EXIF). Downstream consumers do NOT auto-orient:
//   - pdf-lib embeds raw bytes verbatim → regenerate/worker PDF shows sideways
//   - cover-fit math reads pre-rotation width/height → wrong aspect
// So at upload we redraw every raster image through <img> + canvas (the SAME
// decoder the editor uses) and store that. The stored file becomes the single
// upright source of truth — the PDF reproduces the editor pixel-for-pixel
// without reading EXIF at export (it may be missing or stripped).
//
// Orientation only: full resolution is preserved (no downscaling) and we
// re-encode at high quality. HEIC/HEIF are skipped (browsers can't <img>-decode
// them; the server converts those) and PNG is skipped (no EXIF orientation).

const JPEG_QUALITY = 0.95; // high quality; orientation fix must not degrade print

function isHeic(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = url;
  });
}

/**
 * Returns a new File whose pixels are exactly what the browser <img> displays
 * (upright; EXIF baked in and dropped). Skips HEIC (server-converted) and PNG
 * (no orientation). Fails soft to the original file on any error so an upload is
 * never blocked.
 */
export async function normalizeJpegOrientation(file: File): Promise<File> {
  try {
    if (isHeic(file)) return file;
    if (!file.type.startsWith('image/')) return file;
    if (file.type === 'image/png') return file;

    // Decode via <img> — the browser applies EXIF orientation (incl. garbled
    // tags exifr can't parse), so the canvas captures the upright pixels that
    // the editor itself shows. This is the source of truth, not the EXIF value.
    const url = URL.createObjectURL(file);
    let img: HTMLImageElement;
    try {
      img = await loadImage(url);
    } finally {
      URL.revokeObjectURL(url);
    }

    // Full resolution — orientation only, NEVER downscale (preserve print res).
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return file;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) return file;

    // Preserve filename so server-side path generation still works.
    const newName = file.name.replace(/\.(heic|heif|png|webp)$/i, '.jpg');
    return new File([blob], newName.endsWith('.jpg') ? newName : `${newName}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    // Any failure → upload the original. Better than blocking the upload.
    return file;
  }
}
